import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import type { DashboardOverviewDto, HubEvent, HubHistoryEvent } from '@aiai/shared';
import { fetchMyOverview } from '../api';
import { qk } from '../api/queryKeys';
import { NewPrototypeModal } from '../components/NewPrototypeModal';
import { ProjectPickerModal } from '../components/ProjectPickerModal';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useOrg } from '../org/useOrg';
import { useCapabilities } from '../hooks/useCapabilities';
import { workspacePath } from '../lib/workspacePath';
import { isWorkingRun } from '../hub/layout';
import { needsYouItems } from '../hub/needsYou';
import { Universe } from '../hub/Universe';
import { MissionControlHud } from '../hub/MissionControlHud';
import { useWorkspaceFuel } from '../hub/useWorkspaceFuel';
import { fastestLaunch } from '../hub/velocity';
import { PlanetPeekCard } from '../hub/PlanetPeekCard';
import { HubRail } from '../hub/HubRail';
import { LiveDrawer } from '../hub/LiveDrawer';
import { readView, skipFreshLoadView, writeView, type SavedView } from '../hub/viewMemory';
import type { Camera } from '../hub/camera';
import { feedFromRuns } from '../hub/feed';
import { useFeed } from '../hub/useFeed';
import { useHubSocket } from '../hooks/useHubSocket';
import { applyHubEvent, type LiveActivity } from '../hub/liveActivity';
import { useDebouncedInvalidate } from '../hub/useDebouncedInvalidate';
import { EFFECT_TTL_MS, feedFromHubEvent, homeEffects, type HubEffect } from '../hub/hubLive';
import { PlaybackCard } from '../hub/PlaybackCard';
import { useReplay } from '../hub/useReplay';
import { cueFromHubEvent, type TvCue } from '../hub/tvDirector';
import { snapshotCues } from '../hub/tvCues';
import { useTvDirector } from '../hub/useTvDirector';
import { TvChip } from '../hub/TvChip';
import { usePlanetsCan } from '../hooks/usePlanetCapabilities';
import { useLaunchMission } from '../components/LaunchMissionContext';

export function OverviewPage() {
  useDocumentTitle('Mission control');
  const navigate = useNavigate();
  const { orgs } = useOrg();
  const { canInAny, workspacesWith } = useCapabilities();
  const [picker, setPicker] = useState<'loop' | null>(null);
  const [showPrototypeModal, setShowPrototypeModal] = useState(false);

  const overviewQuery = useQuery({
    queryKey: qk.myOverview,
    queryFn: fetchMyOverview,
    refetchInterval: 10_000,
  });

  const overview = overviewQuery.data;
  const active = useMemo(() => overview?.activeRuns ?? [], [overview]);
  const projects = useMemo(() => overview?.projects ?? [], [overview]);

  // Agents actually working (started, not gated) — the only runs that move,
  // count as "live", rank a planet up, or can be stopped.
  const workingRuns = useMemo(() => active.filter(isWorkingRun), [active]);

  const planets = useMemo(() => overview?.planets ?? [], [overview]);
  const planetCan = usePlanetsCan(planets);
  const canLaunch = planets.some((p) => planetCan(p, 'issue:create'));
  const { openLaunch } = useLaunchMission();

  // Each planet's count is exact; the overview's issue list is capped.
  const openIssueCount = useMemo(() => planets.reduce((n, p) => n + p.openIssueCount, 0), [planets]);

  const needsYou = useMemo(() => needsYouItems(planets), [planets]);
  const fastest = useMemo(() => fastestLaunch(planets), [planets]);
  // Back/Forward restores the view: it is remembered per history entry (location.key) in
  // sessionStorage. A fresh visit gets a new key and starts fitted; TV mode skips the memory.
  const location = useLocation();
  const viewKeyRef = useRef(location.key);
  viewKeyRef.current = location.key;
  const [saved] = useState<SavedView | null>(() => {
    // Claimed on every first mount (even in TV mode), so only the document's first mount can skip.
    const fresh = skipFreshLoadView(location.key);
    return fresh || new URLSearchParams(location.search).get('tv') === '1' ? null : readView(location.key);
  });
  const [selectedId, setSelectedId] = useState<string | null>(() => saved?.selectedId ?? null);
  // A selected planet that leaves the scene is forgotten, so its card can't pop back open later.
  useEffect(() => {
    if (selectedId && overview && !planets.some((p) => p.id === selectedId)) setSelectedId(null);
  }, [planets, selectedId, overview]);
  const sceneStatus = overview ? 'ready' : overviewQuery.isError ? 'error' : 'loading';

  // TV mode (?tv=1): an unattended camera that follows the action. The URL is the one source of truth.
  const [searchParams, setSearchParams] = useSearchParams();
  const tvOn = searchParams.get('tv') === '1';
  const tvOnRef = useRef(tvOn);
  tvOnRef.current = tvOn;
  const setTv = useCallback(
    (on: boolean) =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (on) next.set('tv', '1');
          else next.delete('tv');
          return next;
        },
        { replace: true },
      ),
    [setSearchParams],
  );
  const setTvRef = useRef(setTv);
  setTvRef.current = setTv;
  const tvWorking = useMemo(() => [...new Set(workingRuns.map((r) => r.projectId))], [workingRuns]);
  const tvPlanets = useMemo(() => new Set(planets.map((p) => p.id)), [planets]);
  const { cameraShot, push: tvPush } = useTvDirector({ enabled: tvOn, working: tvWorking, planets: tvPlanets });
  // Fit-the-universe cuts the page asks for itself (replay Play, leaving TV). Keys start high so they never
  // collide with the TV director's own counter when the camera switches between the two sources.
  const [fitShot, setFitShot] = useState<{ projectId: null; key: number } | undefined>(undefined);
  const requestFit = useCallback(() => setFitShot((s) => ({ projectId: null, key: (s?.key ?? 1_000_000) + 1 })), []);
  // Leaving TV mode: fit the camera, and hand focus back to the HUD's TV button once it has remounted.
  const wasTv = useRef(false);
  useEffect(() => {
    if (wasTv.current && !tvOn) {
      requestFit();
      document.querySelector<HTMLButtonElement>('button[aria-label="TV mode"]')?.focus();
    }
    wasTv.current = tvOn;
  }, [tvOn, requestFit]);
  // The remembered view: written only when the universe reports a change or the selection moves.
  const view = useRef<SavedView>({ selectedId, camera: saved?.camera ?? null });
  const remember = useCallback((patch: Partial<SavedView>) => {
    view.current = { ...view.current, ...patch };
    if (!tvOnRef.current) writeView(viewKeyRef.current, view.current);
  }, []);
  const onViewChange = useCallback((v: { camera: Camera }) => remember({ camera: v.camera }), [remember]);
  const rememberedSelection = useRef(selectedId);
  useEffect(() => {
    if (rememberedSelection.current === selectedId) return;
    rememberedSelection.current = selectedId;
    remember({ selectedId });
  }, [selectedId, remember]);
  const pushCues = useCallback(
    (cues: TvCue[]) => {
      if (tvOnRef.current) for (const c of cues) tvPush(c);
    },
    [tvPush],
  );
  useEffect(() => {
    if (!tvOn) return;
    // Everything fullscreen is best-effort: it may be missing, throw, or be refused, and TV mode works without it.
    let entered = !!document.fullscreenElement;
    const syncFullscreen = () => {
      if (document.fullscreenElement) entered = true;
      else if (entered) setTvRef.current(false); // the browser's own Esc left fullscreen
    };
    try {
      if (!document.fullscreenElement) {
        const asked = document.documentElement.requestFullscreen?.();
        void Promise.resolve(asked).then(syncFullscreen, () => {});
      }
    } catch {
      // not granted
    }
    const exit = () => setTvRef.current(false);
    document.addEventListener('pointerdown', exit);
    document.addEventListener('keydown', exit);
    document.addEventListener('fullscreenchange', syncFullscreen);
    return () => {
      document.removeEventListener('pointerdown', exit);
      document.removeEventListener('keydown', exit);
      document.removeEventListener('fullscreenchange', syncFullscreen);
      try {
        if (document.fullscreenElement) void Promise.resolve(document.exitFullscreen?.()).catch(() => {});
      } catch {
        // already out
      }
    };
  }, [tvOn]);

  // One-shot effects on the universe (live events and replays): finished and same-id ones are dropped.
  const [effects, setEffects] = useState<HubEffect[]>([]);
  const addEffects = useCallback((fresh: HubEffect[]) => {
    if (fresh.length === 0) return;
    const now = performance.now();
    setEffects((prev) => [...prev.filter((x) => now - x.at < EFFECT_TTL_MS && !fresh.some((f) => f.id === x.id)), ...fresh]);
  }, []);

  // Each new snapshot: TV distress cues (a planet that newly needs you), and the working agents'
  // lines on the feed (the first snapshot seeds it; shared ids, e.g. lineId, collapse a line
  // that the live /hub events below also deliver).
  const [feed, pushFeed, dropFeed] = useFeed();
  const prevOverview = useRef<DashboardOverviewDto | undefined>(undefined);
  useEffect(() => {
    if (!overview) return;
    const prev = prevOverview.current;
    const now = Date.now();
    pushCues(snapshotCues(prev, overview, now));
    pushFeed(feedFromRuns(prev?.activeRuns, overview.activeRuns, now, (r) => workspacePath(r.workspace.slug, `/runs/${r.id}`)));
    prevOverview.current = overview;
  }, [overview, pushCues, pushFeed]);

  // Live /hub events: instant feed lines + comets, and a debounced snapshot refresh for the
  // events that change state (a log line changes nothing the snapshot shows but the feed).
  const refreshOverview = useDebouncedInvalidate([qk.myOverview]);
  const [liveActivity, setLiveActivity] = useState<LiveActivity>({});
  const hubSubs = useMemo(() => orgs.map((o) => ({ orgId: o.id })), [orgs]);
  const overviewRef = useRef(overview);
  overviewRef.current = overview;
  const onHubEvent = useCallback(
    (e: HubEvent) => {
      const ov = overviewRef.current;
      const slug = orgs.find((o) => o.id === e.orgId)?.slug;
      const issueCode = (id: string) => ov?.issues.find((i) => i.id === id)?.code ?? null;
      const href = (ev: HubEvent) =>
        ev.type === 'pr.merged' ? (ev.prUrl ?? null) : ev.runId && slug ? workspacePath(slug, `/runs/${ev.runId}`) : null;
      const entry = feedFromHubEvent(e, href, Date.now(), issueCode);
      if (entry) pushFeed([entry]);
      addEffects(homeEffects(e, performance.now()));
      const cue = cueFromHubEvent(e, Date.now());
      if (cue) pushCues([cue]);
      setLiveActivity((p) => applyHubEvent(p, e));
      if (e.type !== 'run.line') refreshOverview();
    },
    [orgs, pushFeed, addEffects, refreshOverview, pushCues],
  );
  useHubSocket(hubSubs, onHubEvent);
  const fuel = useWorkspaceFuel(orgs);

  // "While you were away": replays the history on the universe (effects + feed) with the camera left alone.
  const { offer: replayOffer, dismiss: dismissReplay } = useReplay();
  // Closing the replay also clears the lines it put on the live feed.
  const closeReplay = useCallback(() => {
    dismissReplay();
    dropFeed('replay:');
  }, [dismissReplay, dropFeed]);
  // Playback (the HUD button) replays any recent period. One card at a time: opening it replaces the
  // away offer, and closing it also drops an offer that arrived meanwhile.
  const [playbackOpen, setPlaybackOpen] = useState(false);
  const openPlayback = useCallback(() => {
    closeReplay();
    setPlaybackOpen(true);
  }, [closeReplay]);
  const closePlayback = useCallback(() => {
    closeReplay();
    setPlaybackOpen(false);
  }, [closeReplay]);
  const dropReplayFeed = useCallback(() => dropFeed('replay:'), [dropFeed]);
  const onReplayEffect = useCallback((fx: HubEffect) => addEffects([fx]), [addEffects]);
  const replayHref = useCallback(
    (e: HubHistoryEvent) => {
      if (e.type === 'pr.merged') return e.prUrl ?? null;
      const slug = orgs.find((o) => o.id === e.orgId)?.slug;
      return e.runId && slug ? workspacePath(slug, `/runs/${e.runId}`) : null;
    },
    [orgs],
  );

  // A user who belongs to no workspace gets the dedicated ask-for-an-invite
  // screen, not an empty dashboard.
  if (overviewQuery.isFetched && orgs.length === 0) {
    return <Navigate to="/no-workspace" replace />;
  }

  // Owner-only create actions are keyed on "may create in at least one
  // workspace"; the create modal's own workspace selector handles the target.
  const canNewPrototype = canInAny('prototype:manage');
  const canNewLoop = canInAny('loop:manage');
  const loopWorkspaceIds = new Set(workspacesWith('loop:manage').map((o) => o.id));
  const loopProjects = projects.filter((p) => loopWorkspaceIds.has(p.workspace.id));
  // Only offer START "New issue" when some planet (repo or prototype) lets the caller
  // create issues (owner, or a contributor grant); the Launch dialog (the same one as
  // the jump palette's) lists planets.
  // Resolved against each planet's OWN workspace (role + grants), not the active one.
  // Same rule as the Projects page: repo projects need a pro workspace you manage.
  const canCreateProject = workspacesWith('project:manage').some((o) => o.plan === 'pro');
  const showEmptyCta = sceneStatus === 'ready' && planets.length === 0 && (canCreateProject || canNewPrototype);

  return (
    <>
      {/* The scene takes the full width at every size; the rail is a drawer over it. */}
      <div className="flex flex-col lg:h-full">
        <section
          aria-label="Agent hub"
          className={
            tvOn
              ? 'fixed inset-0 z-40 flex h-dvh w-screen flex-col bg-surface-2'
              : 'relative flex min-h-[60vh] flex-col bg-surface-2 lg:h-full lg:min-h-0'
          }
        >
          {/* Overlays sit in normal flow on phones (so nothing covers the scene) and float over it from sm up. */}
          {tvOn && <TvChip onExit={() => setTv(false)} />}
          {!tvOn && (
          <div className="order-first p-2 sm:pointer-events-none sm:absolute sm:left-4 sm:top-4 sm:z-10 sm:max-w-[calc(100%-10rem)] sm:p-0">
            <MissionControlHud
              loaded={!!overview}
              shipped24h={overview?.shipped24hCount ?? 0}
              spend24hUsd={overview?.spend24hUsd ?? 0}
              openIssueCount={openIssueCount}
              fuel={fuel}
              fastest={fastest}
              onTv={() => setTv(true)}
              onPlayback={openPlayback}
            />
          </div>
          )}
          {/* A definite height below lg (the universe fills its box): the screen under the header and
              the phone HUD row, at least 60vh. The flexed row on lg. */}
          <div className={tvOn ? 'h-full min-h-0 flex-1' : 'relative h-[max(60vh,calc(100dvh-7rem))] shrink-0 lg:h-auto lg:min-h-0 lg:flex-1'}>
            <Universe
              planets={planets}
              activeRuns={active}
              liveActivity={liveActivity}
              effects={effects}
              selectedId={selectedId}
              onSelect={setSelectedId}
              cameraShot={tvOn ? cameraShot : fitShot}
              status={sceneStatus}
              onViewChange={onViewChange}
              initialCamera={saved?.camera ?? null}
              onOpenIssue={(p, issueId) => navigate(workspacePath(p.workspace.slug, `/issues/${issueId}`))}
              renderPeek={(p, { close }) => (
                <PlanetPeekCard
                  planet={p}
                  runs={workingRuns.filter((r) => r.projectId === p.id)}
                  showWorkspace={orgs.length > 1}
                  onClose={close}
                />
              )}
            />
            {!tvOn && (
              <LiveDrawer working={workingRuns.length} needsYou={needsYou.length}>
                <HubRail
                  needsYou={needsYou}
                  feed={feed}
                  stoppable={workingRuns.length}
                  start={
                    <div className="flex flex-wrap items-center gap-2">
                      {canLaunch && (
                        <button type="button" className="btn-primary" onClick={openLaunch}>
                          <Plus className="h-4 w-4" aria-hidden="true" />
                          New issue
                        </button>
                      )}
                      {canNewPrototype && (
                        <button type="button" className="btn-secondary" onClick={() => setShowPrototypeModal(true)}>
                          <Plus className="h-4 w-4" aria-hidden="true" />
                          New prototype
                        </button>
                      )}
                      {canNewLoop && (
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={() => setPicker('loop')}
                          disabled={loopProjects.length === 0}
                        >
                          <Plus className="h-4 w-4" aria-hidden="true" />
                          New loop
                        </button>
                      )}
                    </div>
                  }
                />
              </LiveDrawer>
            )}
          </div>
          {!tvOn && playbackOpen ? (
            <PlaybackCard
              mode={{ kind: 'playback' }}
              onEffect={onReplayEffect}
              onFeed={pushFeed}
              onClose={closePlayback}
              onPlay={requestFit}
              onReset={dropReplayFeed}
              hrefFor={replayHref}
            />
          ) : !tvOn && replayOffer ? (
            <PlaybackCard
              mode={{ kind: 'offer', offer: replayOffer }}
              onEffect={onReplayEffect}
              onFeed={pushFeed}
              onClose={closeReplay}
              onPlay={requestFit}
              hrefFor={replayHref}
            />
          ) : null}
          {showEmptyCta && (
            <div className="flex flex-wrap items-center justify-center gap-2 px-3 pb-4">
              {canCreateProject && (
                <Link to="/projects" className="btn-primary">
                  Create project
                </Link>
              )}
              {canNewPrototype && (
                <button type="button" className="btn-secondary" onClick={() => setShowPrototypeModal(true)}>
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  New prototype
                </button>
              )}
            </div>
          )}
        </section>
      </div>

      <ProjectPickerModal
        title="New loop in…"
        projects={loopProjects}
        open={picker !== null}
        onClose={() => setPicker(null)}
        hrefFor={(p) => workspacePath(p.workspace.slug, `/projects/${p.code ?? p.id}/loops/new`)}
      />

      <NewPrototypeModal
        open={showPrototypeModal}
        onClose={() => setShowPrototypeModal(false)}
        onCreated={(p, workspaceSlug) => navigate(workspacePath(workspaceSlug, `/prototypes/${p.code ?? p.id}`))}
      />
    </>
  );
}
